import { WSServiceError } from "../../../utils/serviceHelper";
import { useWSQuery } from "../../helpers";
import {
  RequirementDefinition,
  SharedFileRequestContextType
} from "@wingspanhq/wingspan-api-sdk";
// Imported from the registry module, NOT the `@query` barrel — this hook sits
// inside the barrel's import graph (via the registry's invalidation keys), so
// a barrel import here closes a module cycle.
import { queriesRequirementDefinitions } from "../../../v3/query/queriesRequirementDefinitions";
import {
  RequirementDefinitionDataSourceSettingsItem,
  useQueryRequirementDefinitionsSettings
} from "./useQueryRequirementDefinitionsSettings";
import { QUERY_EXTENDED_REQUIREMENT_DEFINITION } from "../keys";

export { QUERY_EXTENDED_REQUIREMENT_DEFINITION };

export interface ExtendedRequirementDefinition extends RequirementDefinition {
  /**
   * The settings resource the definition's `dataSourceId` points at. Named
   * `dataSourceSettings`, not `dataSource`, to avoid colliding with the SDK's own
   * read-only `RequirementDefinition.dataSource` (added in 3.3.0), which projects
   * only the shared-file shape — this union additionally covers document
   * templates, background-check requests and insurance-monitor settings.
   */
  dataSourceSettings?: RequirementDefinitionDataSourceSettingsItem & {
    contextType?: SharedFileRequestContextType;
  };
}

export const useQueryExtendedRequirementDefinition = (
  requirementDefinitionId: string
) => {
  const queryRequirement =
    queriesRequirementDefinitions.getRequirementDefinition.useQuery({
      requirementDefinitionId
    });
  const querySettings = useQueryRequirementDefinitionsSettings({
    enabled: queryRequirement.isSuccess,
    requirementDefinitions: queryRequirement.data
      ? [queryRequirement.data]
      : [],
    fetch: "all"
  });

  return useWSQuery<ExtendedRequirementDefinition, WSServiceError>(
    [QUERY_EXTENDED_REQUIREMENT_DEFINITION, requirementDefinitionId],
    () => {
      const requirement = queryRequirement.data!;

      const extendedRequirement = {
        ...requirement,
        dataSourceSettings: querySettings.data?.[requirement.dataSourceId!]
      };

      return Promise.resolve(extendedRequirement);
    },
    {
      enabled: queryRequirement.isSuccess && querySettings.isFetched
    }
  );
};
